import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { TreeItem } from './tree-item';
import { ShowMoreButton } from './show-more-button';

const MAX_VISIBLE_ITEMS = 10;

export const TreeNavigation = ({
    id: treeId,
    tree,
    isNarrow,
    onShowMoreClick,
    path,
    onItemClick,
    showMoreText
}) => {
    const [expanded, setExpanded] = useState(false);

    if (!tree || tree.length === 0) {
        return null;
    }

    const isTopLevel = !path || path.length === 0;

    return (
        <ol
            className={`plain-list ${isTopLevel ? 'pl-0' : 'pl-2'}`}
            role={isTopLevel ? 'tree' : 'group'}>
            {tree.map((item, index) => {
                const {
                    id: itemId,
                    name,
                    url,
                    isActive,
                    count,
                    children
                } = item;
                const isHidden = index >= MAX_VISIBLE_ITEMS && !expanded;
                return (
                    <li
                        key={itemId}
                        role="treeitem"
                        aria-hidden={isHidden}
                        aria-selected={isActive}
                        className={isHidden ? 'd-none' : 'd-block'}>
                        <TreeItem
                            id={itemId}
                            treeId={treeId}
                            name={name}
                            url={url}
                            path={path}
                            isActive={isActive}
                            isNarrow={isNarrow}
                            onClick={onItemClick}
                            count={children?.length > 0 ? null : count}
                        />
                        {children?.length > 0 && (
                            // Recursively render tree by calling self
                            <TreeNavigation
                                id={treeId}
                                tree={children}
                                isNarrow={isNarrow}
                                className="ml-0 pl-2"
                                onItemClick={onItemClick}
                                onShowMoreClick={onShowMoreClick}
                                showMoreText={showMoreText}
                                path={[...path, name]}
                            />
                        )}
                    </li>
                );
            })}
            <li>
                {tree?.length > MAX_VISIBLE_ITEMS && !expanded ? (
                    <ShowMoreButton
                        onClick={() => {
                            setExpanded(true);
                            if (onShowMoreClick) {
                                onShowMoreClick({ path });
                            }
                        }}>
                        {showMoreText}
                    </ShowMoreButton>
                ) : null}
            </li>
        </ol>
    );
};

const treeShape = {
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired,
    url: PropTypes.string,
    isActive: PropTypes.bool
};
treeShape.children = PropTypes.arrayOf(PropTypes.shape(treeShape));

TreeNavigation.propTypes = {
    id: PropTypes.string.isRequired,
    tree: treeShape.children,
    isNarrow: PropTypes.bool,
    showMoreText: PropTypes.string,
    path: PropTypes.arrayOf(PropTypes.string),
    onItemClick: PropTypes.func,
    onShowMoreClick: PropTypes.func
};

TreeNavigation.defaultProps = {
    isNarrow: false,
    path: []
};
